// 통합 API 유틸리티
// setupProxy.js 의존성을 완전히 제거하기 위한 공통 함수들

/**
 * 401 표식 — 쓰기 헬퍼가 /signin 으로 보내며 돌려주는 값.
 * `null` 을 쓰면 "성공했는데 data 가 비어 있는 응답"(DELETE·마감·재오픈이 흔히 그렇다)과
 * 구분되지 않아 성공을 조용한 무동작으로 삼킨다. 여기 하나만 두는 이유: Symbol 은 모듈마다
 * 새로 만들어지므로, 두 API 모듈을 함께 쓰는 화면에서 비교가 어긋난다.
 */
export const AUTH_REDIRECT = Symbol('auth-redirect');

// API Base URL - 환경변수가 없으면 기본값 사용
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Access Token 가져오기 함수 (AuthContext와 독립적)
const getAccessTokenFromStorage = () => {
  try {
    return localStorage.getItem('accessToken');
  } catch (error) {
    console.error('AccessToken 조회 오류:', error);
    return null;
  }
};

// 통합 API 호출 함수
export const fetchWithTokenRefresh = async (url, options = {}, getAccessToken = null) => {
  try {
    // 토큰 가져오기 - AuthContext의 getAccessToken이 있으면 사용, 없으면 localStorage에서 직접 가져오기
    let token = null;
    if (getAccessToken && typeof getAccessToken === 'function') {
      token = await getAccessToken();
    } else {
      token = getAccessTokenFromStorage();
      if (token) {
        token = `Bearer ${token}`;
      }
    }

    if (!token) {
      // 토큰이 없으면 로그인 페이지로 리다이렉트
      window.location.href = '/signin';
      return null;
    }

    // URL 구성 - 절대 경로로 통일
    const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;

    // API 호출
    const response = await fetch(fullUrl, {
      ...options,
      headers: {
        'Authorization': token,
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include',
    });

    // 401 에러 처리
    if (response.status === 401) {
      // 토큰 만료 또는 무효 - 로그인 페이지로 리다이렉트
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      window.location.href = '/signin';
      return null;
    }

    // 다른 HTTP 에러 처리
    if (!response.ok) {
      let errorMessage = `API 호출 실패: ${response.status}`;
      
      // 사용자 친화적 에러 메시지 생성
      if (response.status === 400) {
        errorMessage = '잘못된 요청입니다. 입력 정보를 확인해주세요.';
      } else if (response.status === 403) {
        errorMessage = '접근 권한이 없습니다.';
      } else if (response.status === 404) {
        errorMessage = '요청하신 데이터를 찾을 수 없습니다.';
      } else if (response.status === 500) {
        errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
      }
      
      try {
        const errorData = await response.text();
        console.error(`API 에러 응답 (${response.status}):`, errorData);
        // 개발 환경에서만 상세 에러 정보 표시
        if (process.env.NODE_ENV === 'development') {
          errorMessage += ` - ${errorData}`;
        }
      } catch (e) {
        console.error('에러 응답 읽기 실패:', e);
      }
      // 읽기 호출도 상태코드로 분기할 수 있게 보존한다. message 문자열은 환경별로 응답 본문이
      // 덧붙을 수 있어 판정값으로 쓰면 안 된다(예: 404=정상적인 빈 이력인 조회).
      const httpError = new Error(errorMessage);
      httpError.httpStatus = response.status;
      throw httpError;
    }

    // 안전한 JSON 파싱
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      try {
        return await response.json();
      } catch (jsonError) {
        console.error('JSON 파싱 오류:', jsonError);
        const textContent = await response.text();
        console.error('응답 내용:', textContent);
        throw new Error('서버 응답을 파싱할 수 없습니다.');
      }
    } else {
      const textContent = await response.text();
      console.error('예상치 못한 응답 형식:', textContent);
      throw new Error('서버에서 JSON 응답을 받지 못했습니다.');
    }
  } catch (error) {
    console.error('API 호출 오류:', error);
    
    // 네트워크 관련 오류를 사용자 친화적으로 변환
    if (error.message.includes('Failed to fetch')) {
      throw new Error('서버에 연결할 수 없습니다. 인터넷 연결을 확인하거나 잠시 후 다시 시도해주세요.');
    }
    if (error.message.includes('NetworkError')) {
      throw new Error('네트워크 오류가 발생했습니다. 연결 상태를 확인해주세요.');
    }
    if (error.message.includes('timeout')) {
      throw new Error('요청 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.');
    }
    
    throw error;
  }
};

// 직접 fetch 호출용 함수 (FormData 등 특수 용도)
export const fetchAPI = async (url, options = {}) => {
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
  return fetch(fullUrl, {
    ...options,
    credentials: 'include',
  });
};

const apiUtils = {
  API_BASE_URL,
  fetchWithTokenRefresh,
  fetchAPI
};

export default apiUtils;
